import { FC, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import axios from 'lib/axios'
import { useNotification } from 'lib/context/notification.context'
import { superAdminRoutes } from 'router/routes'
import TitleDiv from 'views/components/UI/TitleDiv'
import { Button } from 'views/components/UI/Buttons'
import { MainLayout } from 'views/layouts'
import {
  Badge,
  EmptyState,
  HeaderRow,
  MetaRow,
  MetaTag,
  OptionItem,
  OptionsList,
  PageContainer,
  QuestionCard,
  QuestionHeader,
  QuestionTitle,
  QuestionsContainer,
  SectionTitle,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
} from './styled'

type QuestionnaireOption = {
  id: number
  text: string
  position: number
}

type QuestionnaireQuestion = {
  id: number
  text: string
  type: string
  is_required: boolean
  position: number
  options: QuestionnaireOption[]
}

type QuestionnaireDetail = {
  id: number
  name: string
  description: string | null
  status: string
  can_edit?: boolean
  created_at: string | null
  updated_at: string | null
  questions: QuestionnaireQuestion[]
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
}

const STATUS_COLORS: Record<string, string> = {
  draft: '#9a6700',
  published: '#2c7a3f',
  archived: '#7a2832',
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  single_choice: 'Selección única',
  multiple_choice: 'Selección múltiple',
  short_text: 'Respuesta abierta corta',
  long_text: 'Respuesta abierta larga',
  number: 'Numérica',
  date: 'Fecha',
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Sin registro'

  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

const Setting: FC = () => {
  const {
    EditBasicInfoQuestionaire,
  } = superAdminRoutes
  const location = useLocation()
  const { getError, getWarning } = useNotification()
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireDetail | null>(
    null
  )

  const { questionnaireId, questionnaireName } = location.state || {}

  useEffect(() => {
    if (!questionnaireId) return

    axios
      .get(`/company-questionnaire/${questionnaireId}`)
      .then((response) => {
        setQuestionnaire(response?.data?.questionnaire || null)
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          'No fue posible obtener la información del cuestionario'
        getError(message)
      })
  }, [getError, questionnaireId])

  const titleName = questionnaire?.name || questionnaireName || 'Cuestionario'

  const sortedQuestions = useMemo(
    () =>
      [...(questionnaire?.questions || [])].sort(
        (first, second) => first.position - second.position
      ),
    [questionnaire?.questions]
  )

  return (
    <MainLayout>
      <TitleDiv>
        <h2 style={{ fontSize: 32 }}>Detalle del cuestionario {titleName}</h2>
      </TitleDiv>

      {questionnaireId && (
        <PageContainer>
          <HeaderRow>
            <SectionTitle>Información del cuestionario</SectionTitle>
            {questionnaire?.can_edit === false ? (
              <Button
                type="button"
                onClick={() =>
                  getWarning('No puedes editar este cuestionario')
                }
              >
                No puedes editar este cuestionario
              </Button>
            ) : (
              <Link
                to={EditBasicInfoQuestionaire}
                state={{ questionnaireId, questionnaireName: titleName }}
              >
                <Button type="button">Editar cuestionario</Button>
              </Link>
            )}
          </HeaderRow>

          <SummaryGrid>
            <SummaryCard>
              <SectionTitle>Información básica</SectionTitle>
              <SummaryRow>
                <SummaryLabel>Nombre</SummaryLabel>
                <SummaryValue>{titleName}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Descripción</SummaryLabel>
                <SummaryValue>
                  {questionnaire?.description?.trim() ||
                    'Sin descripción registrada'}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Estado</SummaryLabel>
                <Badge statusColor={STATUS_COLORS[questionnaire?.status || 'draft']}>
                  {STATUS_LABELS[questionnaire?.status || 'draft'] || 'Borrador'}
                </Badge>
              </SummaryRow>
            </SummaryCard>

            <SummaryCard>
              <SectionTitle>Resumen</SectionTitle>
              <SummaryRow>
                <SummaryLabel>Total de preguntas</SummaryLabel>
                <SummaryValue>{sortedQuestions.length}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Fecha de creación</SummaryLabel>
                <SummaryValue>{formatDate(questionnaire?.created_at)}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Última actualización</SummaryLabel>
                <SummaryValue>{formatDate(questionnaire?.updated_at)}</SummaryValue>
              </SummaryRow>
            </SummaryCard>
          </SummaryGrid>

          <QuestionsContainer>
            <SectionTitle>Preguntas del cuestionario</SectionTitle>

            {sortedQuestions.length > 0 ? (
              sortedQuestions.map((question) => (
                <QuestionCard key={question.id}>
                  <QuestionHeader>
                    <QuestionTitle>
                      {question.position}. {question.text}
                    </QuestionTitle>
                    <MetaRow>
                      <MetaTag>
                        {QUESTION_TYPE_LABELS[question.type] ||
                          question.type}
                      </MetaTag>
                      <MetaTag>
                        {question.is_required ? 'Obligatoria' : 'Opcional'}
                      </MetaTag>
                    </MetaRow>
                  </QuestionHeader>

                  {question.options?.length > 0 ? (
                    <OptionsList>
                      {[...question.options]
                        .sort((first, second) => first.position - second.position)
                        .map((option) => (
                          <OptionItem key={option.id}>
                            {option.position}. {option.text}
                          </OptionItem>
                        ))}
                    </OptionsList>
                  ) : (
                    <EmptyState>
                      Esta pregunta es abierta y no tiene respuestas predefinidas.
                    </EmptyState>
                  )}
                </QuestionCard>
              ))
            ) : (
              <EmptyState>
                Este cuestionario todavía no tiene preguntas registradas.
              </EmptyState>
            )}
          </QuestionsContainer>

        </PageContainer>
      )}
    </MainLayout>
  )
}

export default Setting
